export type CellData = {
  colTitle: string
  companyName?: string
  companyId?: string
  coords: { row: number; col: number }
  formula: string | null
  rowTitle: string
  text: string | null
  value: string | number | null
  tag?: {
    affected_by_most_recent_doc: boolean
    confirmations: number
    companyId: string
    ids: string[]
    dps: DataPoint[]
    tag: string
  }
}

export type DataPoint = {
  comment?: string | null
  comment_refs?: any[] | null
  comment_type?: string | null
  company_id?: string
  display_label?: string
  doc_period?: string
  doc_type?: string
  id?: string
  masked?: boolean
  normalized_label?: string
  not_meaningful?: boolean
  page_number?: number
  period?: string
  reported_as_label?: string | null
  reported_value?: number
  scaling_factor?: number
  sheet_type?: string
  source_doc?: string
  source_info?: any[]
  subcontext?: string
  value?: number
  units?: string
  tag?: string
}

export interface SpreadsheetCellStyle {
  [key: string]: string | number | boolean | null | undefined
}

export interface HighlightedCell {
  row: number
  col: number
  style: SpreadsheetCellStyle
}

export interface SpreadsheetTableType {
  code: string
  description: string
  use_for_financials: boolean
  use_for_forecasting: boolean
  use_for_classification_list: boolean
  use_for_admin_ui?: boolean
  use_for_flattening?: boolean
  use_for_anchoring?: boolean
}

export interface PinnedDataPoint {
  pin: boolean
  data: CellData
  company?: string | null
  currencyIndication?: string | null
  type: string
}

export interface AuditSelectionTag {
  affected_by_most_recent_doc?: boolean
  confirmations?: number
  companyId?: string
  ids?: string[]
  dps?: DataPoint[]
  tag?: string
  has_movement_timeseries?: boolean
  has_timeseries?: boolean
  reference?: string
  chart_field?: string
  chart_axis_label?: string
  chart_units?: string
  company_id?: string
  type?: string
  format?: string
  sheet_type?: string
  source_type?: string
  units?: string
  scaling_factor?: number
  deviation?: number
  formula?: string | null
}

export type AuditSelectionCell = Omit<Partial<CellData>, 'tag'> & {
  tag?: AuditSelectionTag
  coords?: { row: number; col: number }
  coords2?: { row: number; col: number }
  cellText?: string | null
  cellText2?: string | null
  cellValue?: string | number | null
  cellValue2?: string | number | null
  colTitle2?: string
  reference?: DataPoint
  refs?: DataPoint
  series?: string
}

export interface ChartOptions {
  chartType?: string
  inversionFactor?: number
  zeroBased?: boolean
  sorting?: string | null
  invertedAxis?: boolean
}

export interface HighlightFilters {
  troubleshooting: {
    extractedOnly: boolean
    affectedByFixup: boolean
    affectedByMostRecentDoc: boolean
    periodicity: ('F' | 'P' | 'X')[]
  }
  changes: {
    restatements: boolean
    inconsistencies: boolean
    conflicts: boolean
    userEdits: boolean
  }
}

export interface XaiTag {
  tag: string
  excelMeasures: string[]
  sheetType: string
}

export type ChartingType = 'pit' | 'sp' | 'ts' | 'mov' | undefined

export interface SpreadsheetState {
  highlightedCell: HighlightedCell | null
  activeTypes: { Y: boolean; Q: boolean; H: boolean; L: boolean; T: boolean }
  pinnedDataPoints: PinnedDataPoint[]
  auditing: boolean | 'audit' | 'charts' | undefined
  filtering: boolean
  rowFiltering: boolean
  openAuditPanel: boolean
  openChartsPanel: boolean
  auditSelections: AuditSelectionCell[][]
  tableTypes: SpreadsheetTableType[]
  initializing: boolean
  gridLoaded: boolean
  localLoaded: boolean | undefined
  traceDependents: boolean
  tracePrecedents: boolean
  showGridLines: boolean
  chartingType: ChartingType
  expandedCharts: string[]
  chartOptions: Record<string, ChartOptions>
  currentHighlightFilters: HighlightFilters
  openSearchRange: boolean
  xaiTagList: XaiTag[]
}

export const getDefaultState = (): SpreadsheetState => {
  return {
    highlightedCell: null,
    activeTypes: { Y: true, Q: true, H: false, L: false, T: false },
    pinnedDataPoints: [],
    auditing: false,
    filtering: false,
    rowFiltering: false,
    openAuditPanel: false,
    openChartsPanel: false,
    auditSelections: [],
    tableTypes: [],
    initializing: false,
    gridLoaded: false,
    localLoaded: undefined,
    traceDependents: false,
    tracePrecedents: false,
    showGridLines: false,
    chartingType: 'pit', // pit = point in time, sp = scatter, ts = time series
    expandedCharts: [],
    chartOptions: {},
    currentHighlightFilters: {
      troubleshooting: {
        extractedOnly: false,
        affectedByFixup: false,
        affectedByMostRecentDoc: false,
        periodicity: [],
      },
      changes: {
        restatements: false,
        inconsistencies: false,
        conflicts: false,
        userEdits: true,
      },
    },
    openSearchRange: false,
    xaiTagList: [],
  }
}
export default getDefaultState()
